import { useState, useEffect, useRef, useCallback } from 'react';
import api, { getBackendUrl } from '../config/api';
import { flattenSignalkTree } from '../utils/signalKValueFormat';
import { resolveWidgetSignalkValue } from '../utils/signalkWidgetData';

function getPublicApiBaseUrl() {
  const { protocol, hostname, port } = window.location;
  const portSuffix = port && port !== '80' && port !== '443' ? `:${port}` : '';
  return `${protocol}//${hostname}${portSuffix}`;
}

async function resolveDefaultVesselId(preferredId) {
  if (preferredId) return preferredId;
  try {
    const resp = await api.get('/api/vessels');
    const vessels = resp.data?.vessels ?? (Array.isArray(resp.data) ? resp.data : []);
    return vessels[0]?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * Shared live + database + demo SignalK state for dashboard views.
 * When shareToken is set, polls the public shared-dashboard data API (no JWT / WebSocket).
 */
export function useSignalkDashboardData(vesselId, { enableWebSocket = true, shareToken = null } = {}) {
  const [effectiveVesselId, setEffectiveVesselId] = useState(vesselId ?? null);
  const [signalKData, setSignalKData] = useState({});
  const [signalKFlat, setSignalKFlat] = useState({});
  const [demoData, setDemoData] = useState({});
  const [liveData, setLiveData] = useState({});
  const [wsConnected, setWsConnected] = useState(false);
  const [vesselStreamConnected, setVesselStreamConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const isPublicShare = Boolean(shareToken);

  const pollVesselStreamStatus = useCallback(async (vidParam) => {
    if (isPublicShare) return;
    const vid = vidParam ?? effectiveVesselId;
    if (!vid) return;
    try {
      const res = await api.get(`/api/vessels/${vid}/token/stream-status`);
      setVesselStreamConnected(Boolean(res.data?.stream_connected));
    } catch {
      setVesselStreamConnected(false);
    }
  }, [effectiveVesselId, isPublicShare]);

  useEffect(() => {
    if (isPublicShare) {
      setEffectiveVesselId(vesselId ?? null);
      return undefined;
    }
    let cancelled = false;
    resolveDefaultVesselId(vesselId).then((vid) => {
      if (!cancelled) setEffectiveVesselId(vid);
    });
    return () => {
      cancelled = true;
    };
  }, [vesselId, isPublicShare]);

  const applySharedDataPayload = useCallback((payloadData) => {
    const live = {};
    const flat = {};
    Object.entries(payloadData || {}).forEach(([path, entry]) => {
      if (!path || entry == null || entry.value === undefined || entry.value === null) return;
      const mapped = {
        value: entry.value,
        timestamp: entry.timestamp || new Date().toISOString(),
        units: entry.unit || entry.units || '',
      };
      if (String(entry.source || '').toLowerCase() === 'live') {
        live[path] = mapped;
      } else {
        flat[path] = mapped;
      }
    });
    setLiveData(live);
    setSignalKFlat(flat);
    setSignalKData({});
    const hasLive = Object.keys(live).length > 0;
    setVesselStreamConnected(hasLive);
    setWsConnected(hasLive);
  }, []);

  const loadSharedDashboardData = useCallback(async () => {
    if (!shareToken) return;
    try {
      const response = await fetch(
        `${getPublicApiBaseUrl()}/api/dashboard-sharing/shared/${shareToken}/data`
      );
      if (!response.ok) return;
      const payload = await response.json();
      applySharedDataPayload(payload.data || {});
    } catch (error) {
      console.error('Error loading shared dashboard SignalK data:', error);
    }
  }, [shareToken, applySharedDataPayload]);

  const loadSignalKData = useCallback(async (vidParam) => {
    if (isPublicShare) {
      await loadSharedDashboardData();
      return;
    }

    const vid = vidParam ?? effectiveVesselId;
    if (!vid) return;

    const dbPromise = (async () => {
      try {
        let response;
        try {
          response = await api.get(`/api/vessels/${vid}/signalk/latest`);
        } catch (_) {
          response = await api.get(`/api/vessels/${vid}/signalk/data`);
        }
        const tree = response.data || {};
        const flat = flattenSignalkTree(tree);
        setSignalKData(tree);
        setSignalKFlat(flat);
      } catch (error) {
        console.error('Error loading SignalK database snapshot:', error);
      }
    })();

    dbPromise.finally(() => {
      api.get('/api/demo/snapshot')
        .then((demoResp) => setDemoData(demoResp.data || {}))
        .catch((demoErr) => console.warn('Demo snapshot fetch failed:', demoErr?.message || demoErr));
    });
  }, [effectiveVesselId, isPublicShare, loadSharedDashboardData]);

  const connectWebSocket = useCallback(
    (vidParam) => {
      if (isPublicShare || !enableWebSocket) return;
      const vid = vidParam ?? effectiveVesselId;
      const token = localStorage.getItem('token');
      if (!token || !vid) return;

      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      const backend = getBackendUrl().replace(/\/$/, '');
      const wsBase = backend.replace(/^http/, 'ws');
      const wsUrl = `${wsBase}/stream?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setWsConnected(true);
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ action: 'subscribe', vessel_id: vid }));
        }
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          const mergeFlatPaths = (base, flatPaths) => {
            if (!flatPaths || typeof flatPaths !== 'object') return base;
            const updated = { ...base };
            Object.entries(flatPaths).forEach(([path, entry]) => {
              if (!path) return;
              const value =
                entry && typeof entry === 'object' && 'value' in entry ? entry.value : entry;
              if (value === null || value === undefined) return;
              updated[path] = {
                value,
                timestamp: (entry && entry.timestamp) || message.timestamp || new Date().toISOString(),
                units: (entry && (entry.units || entry.unit)) || '',
              };
            });
            return updated;
          };

          if (message.type === 'delta' && message.delta) {
            setLiveData((prev) => {
              const updated = { ...prev };
              if (message.delta.updates) {
                message.delta.updates.forEach((update) => {
                  if (!update.values) return;
                  update.values.forEach((pathValue) => {
                    if (pathValue.value === null || pathValue.value === undefined) return;
                    updated[pathValue.path] = {
                      value: pathValue.value,
                      timestamp: update.timestamp || message.timestamp,
                      units: pathValue.unit || '',
                    };
                  });
                });
              }
              return updated;
            });
            // Receiving deltas means the boat stream is active even if HTTP poll lags.
            setVesselStreamConnected(true);
          } else if (message.type === 'snapshot' && message.data) {
            setLiveData((prev) => mergeFlatPaths(prev, message.data));
            if (message.data && Object.keys(message.data).length > 0) {
              setVesselStreamConnected(true);
            }
          } else if (message.type === 'subscribed') {
            if (typeof message.stream_active === 'boolean') {
              setVesselStreamConnected(message.stream_active);
            }
          }
        } catch (error) {
          console.error('Error processing WebSocket message:', error);
        }
      };

      ws.onclose = () => {
        setWsConnected(false);
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
        }
        reconnectTimerRef.current = setTimeout(() => connectWebSocket(vid), 5000);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      wsRef.current = ws;
    },
    [enableWebSocket, effectiveVesselId, isPublicShare]
  );

  useEffect(() => {
    if (isPublicShare) {
      if (!shareToken) return undefined;
      loadSharedDashboardData();
      const poll = setInterval(loadSharedDashboardData, 5000);
      return () => clearInterval(poll);
    }

    if (!effectiveVesselId) return undefined;
    loadSignalKData(effectiveVesselId);
    connectWebSocket(effectiveVesselId);
    pollVesselStreamStatus(effectiveVesselId);
    const streamPoll = setInterval(() => pollVesselStreamStatus(effectiveVesselId), 30000);
    return () => {
      clearInterval(streamPoll);
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [
    isPublicShare,
    shareToken,
    effectiveVesselId,
    loadSignalKData,
    loadSharedDashboardData,
    connectWebSocket,
    pollVesselStreamStatus,
  ]);

  const resolveWidgetValue = useCallback(
    (dataPath, widgetConfig = {}) => {
      const trimmedPath = typeof dataPath === 'string' ? dataPath.trim() : dataPath;
      return resolveWidgetSignalkValue({
        dataPath: trimmedPath,
        widgetConfig,
        liveData,
        wsConnected,
        vesselStreamConnected,
        databaseData: signalKData,
        databaseFlat: signalKFlat,
        demoData,
      });
    },
    [liveData, wsConnected, vesselStreamConnected, signalKData, signalKFlat, demoData]
  );

  return {
    vesselId: effectiveVesselId,
    signalKData,
    signalKFlat,
    demoData,
    liveData,
    wsConnected,
    vesselStreamConnected,
    loadSignalKData,
    connectWebSocket,
    resolveWidgetValue,
  };
}
